import { ManageRolesTableDataItem } from "PFApp/activities/manage_roles/manage_roles.types";
import { TemplateKey } from "PFApp/constants/templates";
import { useManageRolesStore } from "PFApp/stores/manage_roles";
import { RolesFormKey } from "PFApp/types/activity_form/activity_form_schema";
import { getColumnConfig } from "PFApp/workflow/parts/activities_table/get_column_config";
import { useCellFactory } from "PFApp/workflow/parts/activities_table/use_cell_factory";
import { AccessorFnColumnDef, ColumnHelper } from "PFComponents/table/table.types";
import { TableColumn } from "PFCore/types";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { getManageRolesColumnsOrder } from "../manage_roles.utils";
import { AdditionalColumnCell } from "../parts/additional_column_cell";
import { createBookedWmColumn } from "../parts/booked_wm_cell";
import { createOriginalWmColumn } from "../parts/original_wm_cell";
import { createSuggestedWmColumn } from "../parts/suggested_wm_cell";

type UseManageRolesColumns = {
  rolesFormKey: RolesFormKey;
};

export const useManageRolesColumns = ({ rolesFormKey }: UseManageRolesColumns) => {
  const { t } = useTranslation("activities", { keyPrefix: "manageRoles.columns" });
  const renderCell = useCellFactory(TemplateKey.Role);

  const availableColumns = useManageRolesStore((state) => state.availableColumns);
  const selectedColumnNames = useManageRolesStore((state) => state.selectedColumns);

  const selectedColumns = useMemo(
    () =>
      selectedColumnNames
        .map((name) => availableColumns.find((column) => column.name === name))
        .filter((column): column is TableColumn => !!column),
    [selectedColumnNames, availableColumns]
  );

  const generateColumns = useCallback(
    (columnHelper: ColumnHelper<ManageRolesTableDataItem>) => {
      const columnsById: Record<string, AccessorFnColumnDef<ManageRolesTableDataItem, any>> = {
        original_wm: createOriginalWmColumn(columnHelper, rolesFormKey, t("originalWm")),
        suggested_wm: createSuggestedWmColumn(columnHelper, rolesFormKey, t("suggestedWm")),
        booked_wm: createBookedWmColumn(columnHelper, rolesFormKey, t("bookedWm"))
      };

      selectedColumns.forEach((column) => {
        columnsById[column.name] = columnHelper.accessor((row) => row, {
          ...getColumnConfig(column, []),
          cell: (info) => (
            <AdditionalColumnCell
              column={column}
              rolesFormKey={rolesFormKey}
              renderCell={renderCell}
              info={info}
            />
          ),
          meta: {
            getIsCopyPasteBlocked: () => true
          }
        });
      });

      return getManageRolesColumnsOrder(selectedColumnNames)
        .map((id) => columnsById[id])
        .filter((column): column is NonNullable<typeof column> => !!column);
    },
    [rolesFormKey, t, selectedColumns, selectedColumnNames, renderCell]
  );

  return { generateColumns };
};
